import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { carregarEu, nomeDaEmpresa, type Eu } from '../servicos/eu';
import { useIdentidade } from './useSessao';

/**
 * Identidade da conta e empresas vinculadas, com nome.
 *
 * A `queryKey` leva o `accountId`: o cache é limpo no login e no logout
 * (`useSessao`), mas amarrar a consulta à conta impede que uma sessão veja o
 * cadastro da anterior se alguma limpeza falhar. Não é a mesma preocupação do
 * RN-603 — aqui não há número fiscal —, é o mesmo princípio.
 */
export function useEu(): UseQueryResult<Eu, Error> {
  const identidade = useIdentidade();

  return useQuery({
    queryKey: ['eu', identidade?.accountId ?? null],
    enabled: identidade !== null,
    queryFn: ({ signal }) => carregarEu(signal),
    // Razão social e nome fantasia não mudam durante uma navegação; recarregar a
    // cada montagem de tela seria uma requisição por clique sem nada novo.
    staleTime: 5 * 60_000,
  });
}

/**
 * Nome de exibição de uma empresa (D-009), ou `null` enquanto não se sabe.
 *
 * `null` é "ainda não carregou" ou "não veio na lista" — quem chama decide o que
 * mostrar no lugar. A tela nunca inventa um rótulo: sem nome, mostra o id e diz
 * que é o id.
 */
export function useNomeDaEmpresa(businessId: string | null): string | null {
  const eu = useEu();
  if (businessId === null) return null;

  // A desativada também tem nome: o admin pode estar operando por ela.
  const empresa = [...(eu.data?.empresas ?? []), ...(eu.data?.empresasDesativadas ?? [])].find(
    (item) => item.businessId === businessId,
  );
  return empresa === undefined ? null : nomeDaEmpresa(empresa);
}
